import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const DEFAULT_TRANSITION_MS = 650;
const SQUARE_CONTENT_FONT_FAMILY_MAP = {
  system: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  sans: '"Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: '"Courier New", "Lucida Console", monospace',
  rounded: '"Trebuchet MS", Verdana, sans-serif',
  classic: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
  display: 'Impact, "Arial Black", sans-serif',
  script: '"Comic Sans MS", "Segoe Print", cursive',
  philosopher: 'Philosopher, "Palatino Linotype", "Book Antiqua", Palatino, serif',
};

function clampRange(value, fallback, min, max) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
}

function clampOpacity(value, fallback = 1) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeHexColor(value, fallback = '#000000') {
  const raw = String(value || '').trim();
  const match = raw.match(/^#([0-9a-f]{6})$/i);
  return match ? `#${match[1].toLowerCase()}` : fallback;
}

function normalizeSquareGradientShape(value, fallback = 'linear') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'linear' || raw === 'radial' || raw === 'conic' ? raw : fallback;
}

function normalizeGradientColorAlphas(input, colorCount = 0, fallback = []) {
  const desired = Math.max(0, Math.min(8, Math.round(Number(colorCount) || 0)));
  if (!desired) return [];
  const source = Array.isArray(input) ? input : [];
  const baseline = Array.isArray(fallback) ? fallback : [];
  const result = [];
  for (let index = 0; index < desired; index += 1) {
    result.push(clampOpacity(source[index], clampOpacity(baseline[index], 1)));
  }
  return result;
}

function normalizeSquareMaterialType(value, fallback = 'standard') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'standard' || raw === 'physical' || raw === 'phong' || raw === 'lambert' || raw === 'basic'
    ? raw
    : fallback;
}

function createRuntimeMaterial(materialType = 'standard') {
  const normalizedType = normalizeSquareMaterialType(materialType, 'standard');
  const shared = {
    color: 0xffffff,
    side: THREE.DoubleSide,
    transparent: true,
    toneMapped: true,
  };

  let material;
  if (normalizedType === 'physical') {
    material = new THREE.MeshPhysicalMaterial({
      ...shared,
      roughness: 0.78,
      metalness: 0.08,
      clearcoat: 0,
      transmission: 0,
    });
  } else if (normalizedType === 'phong') {
    material = new THREE.MeshPhongMaterial({
      ...shared,
      shininess: 35,
      specular: new THREE.Color(0x2f2f2f),
    });
  } else if (normalizedType === 'lambert') {
    material = new THREE.MeshLambertMaterial(shared);
  } else if (normalizedType === 'basic') {
    material = new THREE.MeshBasicMaterial(shared);
  } else {
    material = new THREE.MeshStandardMaterial({
      ...shared,
      roughness: 0.78,
      metalness: 0.08,
    });
  }

  if (!material.userData) material.userData = {};
  material.userData.squareMaterialType = normalizedType;
  return material;
}

function inferSquareMaterialType(material) {
  if (!material) return 'standard';
  const fromUserData = normalizeSquareMaterialType(material?.userData?.squareMaterialType, '');
  if (fromUserData) return fromUserData;
  const type = String(material?.type || '').trim();
  if (type === 'MeshPhysicalMaterial') return 'physical';
  if (type === 'MeshPhongMaterial') return 'phong';
  if (type === 'MeshLambertMaterial') return 'lambert';
  if (type === 'MeshBasicMaterial') return 'basic';
  return 'standard';
}

function ensureRuntimeMaterial(mesh, currentMaterial, desiredMaterialType = 'standard') {
  if (!mesh) return currentMaterial || null;
  const targetType = normalizeSquareMaterialType(desiredMaterialType, 'standard');
  const current = currentMaterial || mesh.material;
  const currentType = inferSquareMaterialType(current);
  if (current && currentType === targetType) return current;

  const next = createRuntimeMaterial(targetType);
  if (current) {
    if (current.map) next.map = current.map;
    if (current.displacementMap) next.displacementMap = current.displacementMap;
    try { current.dispose?.(); } catch {}
  }
  mesh.material = next;
  return next;
}

function normalizeSquareContentAlign(value, fallback = 'center') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'left' || raw === 'center' || raw === 'right' ? raw : fallback;
}

function normalizeSquareContentFontFamily(value, fallback = 'system') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'system' || raw === 'sans' || raw === 'serif' || raw === 'mono' || raw === 'rounded' || raw === 'classic' || raw === 'display' || raw === 'script' || raw === 'philosopher'
    ? raw
    : fallback;
}

function resolveSquareContentFontFamily(value, fallback = 'system') {
  const key = normalizeSquareContentFontFamily(value, fallback);
  return SQUARE_CONTENT_FONT_FAMILY_MAP[key] || SQUARE_CONTENT_FONT_FAMILY_MAP.system;
}

function normalizeSquareContentWeight(value, fallback = 600) {
  const allowed = [300, 400, 500, 600, 700, 800, 900];
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  const rounded = Math.round(raw);
  if (allowed.includes(rounded)) return rounded;
  return allowed.reduce((closest, candidate) => (
    Math.abs(candidate - rounded) < Math.abs(closest - rounded) ? candidate : closest
  ), fallback);
}

function normalizeSquareContentBlocks(input, fallbackText = '') {
  const source = Array.isArray(input) ? input : [];
  const blocks = source
    .slice(0, 12)
    .map((entry, index) => {
      const row = (entry && typeof entry === 'object') ? entry : {};
      const fallbackId = `content-${index + 1}`;
      const text = String(row.text || '').slice(0, 6000);
      if (!text.trim()) return null;
      return {
        id: String(row.id || fallbackId).trim().slice(0, 120) || fallbackId,
        text,
        color: normalizeHexColor(row.color, '#ffffff'),
        size: clampRange(row.size, 30, 12, 120),
        weight: normalizeSquareContentWeight(row.weight, 600),
        italic: !!row.italic,
        align: normalizeSquareContentAlign(row.align, 'center'),
        fontFamily: normalizeSquareContentFontFamily(row.fontFamily, 'system'),
        lineHeight: clampRange(row.lineHeight, 1.2, 0.8, 2.4),
        opacity: clampOpacity(row.opacity, 1),
      };
    })
    .filter(Boolean);

  if (blocks.length) return blocks;

  const fallback = String(fallbackText || '').slice(0, 6000);
  if (!fallback.trim()) return [];
  return [{
    id: 'content-1',
    text: fallback,
    color: '#ffffff',
    size: 30,
    weight: 600,
    italic: false,
    align: 'center',
    fontFamily: 'system',
    lineHeight: 1.2,
    opacity: 1,
  }];
}

function normalizeSquareAnimationMode(value, fallback = 'linear') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'linear' || raw === 'non-linear' ? raw : fallback;
}

function normalizeSquareAnimationDirection(value, fallback = 'loop') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'loop' || raw === 'alternate' ? raw : fallback;
}

function normalizeSquareAnimationKeyframes(input) {
  const source = Array.isArray(input) ? input : [];
  const total = source.length;
  return source
    .slice(0, 10)
    .map((entry, index) => {
      const row = (entry && typeof entry === 'object') ? entry : {};
      const fallbackAt = total <= 1 ? 0 : index / Math.max(1, total - 1);
      const rawAt = Number(row.at);
      return {
        at: Number.isFinite(rawAt) ? clampRange(rawAt, fallbackAt, 0, 1) : fallbackAt,
        offsetX: clampRange(row.offsetX, 0, -60, 60),
        offsetY: clampRange(row.offsetY, 0, -60, 60),
        scale: clampRange(row.scale, 1, 0.2, 4),
        rotate: clampRange(row.rotate, 0, -360, 360),
        opacity: clampOpacity(row.opacity, 1),
      };
    })
    .sort((a, b) => a.at - b.at);
}

function resolveSquareAnimationProgress(progress, direction = 'loop') {
  const t = clampRange(progress, 0, 0, 1);
  if (normalizeSquareAnimationDirection(direction, 'loop') === 'alternate') {
    return t <= 0.5 ? t * 2 : (2 - (t * 2));
  }
  return t;
}

function applyAnimationMode(progress, mode = 'linear') {
  const t = clampRange(progress, 0, 0, 1);
  if (normalizeSquareAnimationMode(mode, 'linear') === 'non-linear') {
    return 0.5 - (Math.cos(Math.PI * t) / 2);
  }
  return t;
}

function interpolateSquareAnimationState(
  keyframes = [],
  nowMs = Date.now(),
  durationSeconds = 12,
  animationMode = 'linear',
  animationDirection = 'loop'
) {
  if (!keyframes.length) return {
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotate: 0,
    opacity: 1,
  };

  if (keyframes.length === 1) {
    const only = keyframes[0];
    return {
      offsetX: only.offsetX,
      offsetY: only.offsetY,
      scale: only.scale,
      rotate: only.rotate,
      opacity: only.opacity,
    };
  }

  const durationMs = Math.max(1000, Math.round(Number(durationSeconds || 12) * 1000));
  const cycleProgress = ((nowMs % durationMs) / durationMs);
  const directionalProgress = resolveSquareAnimationProgress(cycleProgress, animationDirection);
  const eased = applyAnimationMode(directionalProgress, animationMode);
  const frames = keyframes.slice().sort((a, b) => a.at - b.at);

  let left = frames[0];
  let right = frames[1] || frames[0];
  if (eased <= frames[0].at) {
    left = frames[0];
    right = frames[1] || frames[0];
  } else if (eased >= frames[frames.length - 1].at) {
    left = frames[frames.length - 2] || frames[frames.length - 1];
    right = frames[frames.length - 1];
  } else {
    for (let index = 0; index < frames.length - 1; index += 1) {
      const a = frames[index];
      const b = frames[index + 1];
      if (eased >= a.at && eased <= b.at) {
        left = a;
        right = b;
        break;
      }
    }
  }

  const denominator = Math.max(0.0001, right.at - left.at);
  const t = clampRange((eased - left.at) / denominator, 0, 0, 1);
  const lerp = (from, to) => from + ((to - from) * t);
  return {
    offsetX: lerp(left.offsetX, right.offsetX),
    offsetY: lerp(left.offsetY, right.offsetY),
    scale: lerp(left.scale, right.scale),
    rotate: lerp(left.rotate, right.rotate),
    opacity: lerp(left.opacity, right.opacity),
  };
}

function wrapTextWithFont(ctx, text, maxWidth, font) {
  const raw = String(text || '');
  if (!raw.trim()) return [];
  const lines = [];
  const paragraphs = raw.split(/\r?\n/g);
  ctx.save();
  ctx.font = font;

  paragraphs.forEach((paragraph) => {
    const words = paragraph.split(/\s+/g).filter(Boolean);
    if (!words.length) {
      lines.push('');
      return;
    }

    let current = words[0];
    for (let index = 1; index < words.length; index += 1) {
      const next = `${current} ${words[index]}`;
      if (ctx.measureText(next).width <= maxWidth) {
        current = next;
      } else {
        lines.push(current);
        current = words[index];
      }
    }
    lines.push(current);
  });

  ctx.restore();
  return lines;
}

function buildFrameGradientStops(frame) {
  const fallbackColors = [
    normalizeHexColor(frame?.color1, '#0b0f16'),
    normalizeHexColor(frame?.color2, '#121b2e'),
  ];
  const gradientColorsRaw = (Array.isArray(frame?.gradientColors) ? frame.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const gradientColors = gradientColorsRaw.length >= 2 ? gradientColorsRaw : fallbackColors;
  const gradientColorAlphas = normalizeGradientColorAlphas(
    frame?.gradientColorAlphas,
    gradientColors.length,
    Array.from({ length: gradientColors.length }, () => 1)
  );

  return gradientColors.map((color, index) => {
    const rgb = new THREE.Color(color);
    return {
      offset: gradientColors.length <= 1 ? 0 : (index / (gradientColors.length - 1)),
      color: `rgba(${Math.round(rgb.r * 255)}, ${Math.round(rgb.g * 255)}, ${Math.round(rgb.b * 255)}, ${clampOpacity(gradientColorAlphas[index], 1).toFixed(3)})`,
    };
  });
}

function createFrameGradient(ctx, width, height, frame) {
  const gradientShape = normalizeSquareGradientShape(frame?.gradientShape, 'linear');
  const gradientAngle = clampRange(frame?.gradientAngle, 90, 0, 360);
  const stops = buildFrameGradientStops(frame);

  let gradient;
  if (gradientShape === 'radial') {
    const radius = Math.max(width, height) * 0.58;
    gradient = ctx.createRadialGradient(width * 0.5, height * 0.5, 0, width * 0.5, height * 0.5, radius);
  } else if (gradientShape === 'conic' && typeof ctx.createConicGradient === 'function') {
    gradient = ctx.createConicGradient((gradientAngle * Math.PI) / 180, width * 0.5, height * 0.5);
  } else {
    const radians = (gradientAngle * Math.PI) / 180;
    const vx = Math.cos(radians);
    const vy = Math.sin(radians);
    const halfDiag = Math.sqrt((width * width) + (height * height)) * 0.5;
    const cx = width * 0.5;
    const cy = height * 0.5;
    gradient = ctx.createLinearGradient(
      cx - (vx * halfDiag),
      cy - (vy * halfDiag),
      cx + (vx * halfDiag),
      cy + (vy * halfDiag),
    );
  }

  stops.forEach((entry) => {
    gradient.addColorStop(entry.offset, entry.color);
  });
  return gradient;
}

function normalizeGeometryType(value, fallback = 'plane') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'plane' || raw === 'sphere' || raw === 'box' || raw === 'triangle' || raw === 'other'
    ? raw
    : fallback;
}

function lerp(from, to, progress) {
  return from + ((to - from) * progress);
}

function mixHexColors(fromHex, toHex, progress) {
  const fromColor = new THREE.Color(normalizeHexColor(fromHex));
  const toColor = new THREE.Color(normalizeHexColor(toHex));
  fromColor.lerp(toColor, clampRange(progress, 1, 0, 1));
  return `#${fromColor.getHexString()}`;
}

function relativeLuminance(hexColor) {
  const color = new THREE.Color(normalizeHexColor(hexColor));
  return (0.2126 * color.r) + (0.7152 * color.g) + (0.0722 * color.b);
}

function colorDistance(colorA, colorB) {
  if (!colorA || !colorB) return 1;

  const dr = Number(colorA.r) - Number(colorB.r);
  const dg = Number(colorA.g) - Number(colorB.g);
  const db = Number(colorA.b) - Number(colorB.b);
  if (!Number.isFinite(dr) || !Number.isFinite(dg) || !Number.isFinite(db)) {
    return 1;
  }

  return Math.sqrt((dr * dr) + (dg * dg) + (db * db));
}

function normalizeKanvasFrame(input = {}, fallback = {}) {
  const source = (input && typeof input === 'object') ? input : {};
  const baseline = (fallback && typeof fallback === 'object') ? fallback : {};

  const fallbackColor1 = normalizeHexColor(baseline.color1, '#0b0f16');
  const fallbackColor2 = normalizeHexColor(baseline.color2, '#121b2e');
  const fallbackGradientColorsRaw = (Array.isArray(baseline.gradientColors) ? baseline.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const fallbackGradientColors = fallbackGradientColorsRaw.length >= 2
    ? fallbackGradientColorsRaw
    : [fallbackColor1, fallbackColor2];
  const gradientColorsRaw = (Array.isArray(source.gradientColors) ? source.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const gradientColors = gradientColorsRaw.length >= 2 ? gradientColorsRaw : fallbackGradientColors;
  const gradientColorAlphas = normalizeGradientColorAlphas(
    source.gradientColorAlphas,
    gradientColors.length,
    normalizeGradientColorAlphas(
      baseline.gradientColorAlphas,
      gradientColors.length,
      Array.from({ length: gradientColors.length }, () => 1)
    )
  );

  return {
    color1: normalizeHexColor(source.color1, fallbackColor1),
    color2: normalizeHexColor(source.color2, fallbackColor2),
    gradientColors,
    gradientColorAlphas,
    gradientShape: normalizeSquareGradientShape(source.gradientShape, normalizeSquareGradientShape(baseline.gradientShape, 'linear')),
    gradientAngle: clampRange(source.gradientAngle, clampRange(baseline.gradientAngle, 90, 0, 360), 0, 360),
    emissiveColor: normalizeHexColor(
      source.emissiveColor,
      normalizeHexColor(baseline.emissiveColor, '#000000')
    ),
    roughness: clampRange(
      source.materialRoughness ?? source.roughness,
      clampRange(baseline.roughness, 0.72, 0, 1),
      0,
      1
    ),
    metalness: clampRange(
      source.materialMetalness ?? source.metalness,
      clampRange(baseline.metalness, 0.18, 0, 1),
      0,
      1
    ),
    emissiveIntensity: clampRange(
      source.materialEmissiveIntensity ?? source.emissiveIntensity,
      clampRange(baseline.emissiveIntensity, 0.04, 0, 8),
      0,
      8
    ),
    opacity: clampRange(
      source.materialOpacity ?? source.opacity,
      clampRange(baseline.opacity, 1, 0.08, 1),
      0.08,
      1
    ),
    clearcoat: clampRange(
      source.materialClearcoat ?? source.clearcoat,
      clampRange(baseline.clearcoat, 0.08, 0, 1),
      0,
      1
    ),
    transmission: clampRange(
      source.materialTransmission ?? source.transmission,
      clampRange(baseline.transmission, 0, 0, 1),
      0,
      1
    ),
    materialType: normalizeSquareMaterialType(source.materialType, normalizeSquareMaterialType(baseline.materialType, 'standard')),
    transitionMs: clampRange(source.transitionMs, clampRange(baseline.transitionMs, DEFAULT_TRANSITION_MS, 80, 5000), 80, 5000),
    backgroundColor: normalizeHexColor(source.backgroundColor, normalizeHexColor(baseline.backgroundColor, '#050505')),
    backgroundOpacity: clampOpacity(source.backgroundOpacity, clampOpacity(baseline.backgroundOpacity, 0.65)),
  };
}

function interpolateFrame(fromFrame, toFrame, progress) {
  const t = clampRange(progress, 1, 0, 1);

  const gradientCount = Math.max(fromFrame.gradientColors.length, toFrame.gradientColors.length, 2);
  const gradientColors = [];
  const gradientColorAlphas = [];
  for (let index = 0; index < gradientCount; index += 1) {
    const fromColor = fromFrame.gradientColors[index]
      || fromFrame.gradientColors[fromFrame.gradientColors.length - 1]
      || fromFrame.color1;
    const toColor = toFrame.gradientColors[index]
      || toFrame.gradientColors[toFrame.gradientColors.length - 1]
      || fromColor;
    gradientColors.push(mixHexColors(fromColor, toColor, t));

    const fromAlpha = clampOpacity(fromFrame.gradientColorAlphas[index], 1);
    const toAlpha = clampOpacity(toFrame.gradientColorAlphas[index], fromAlpha);
    gradientColorAlphas.push(clampOpacity(lerp(fromAlpha, toAlpha, t), toAlpha));
  }

  return {
    color1: mixHexColors(fromFrame.color1, toFrame.color1, t),
    color2: mixHexColors(fromFrame.color2, toFrame.color2, t),
    gradientColors,
    gradientColorAlphas,
    gradientShape: t < 1 ? fromFrame.gradientShape : toFrame.gradientShape,
    gradientAngle: lerp(fromFrame.gradientAngle, toFrame.gradientAngle, t),
    emissiveColor: mixHexColors(fromFrame.emissiveColor, toFrame.emissiveColor, t),
    roughness: lerp(fromFrame.roughness, toFrame.roughness, t),
    metalness: lerp(fromFrame.metalness, toFrame.metalness, t),
    emissiveIntensity: lerp(fromFrame.emissiveIntensity, toFrame.emissiveIntensity, t),
    opacity: clampRange(lerp(fromFrame.opacity, toFrame.opacity, t), toFrame.opacity, 0.08, 1),
    clearcoat: lerp(fromFrame.clearcoat, toFrame.clearcoat, t),
    transmission: lerp(fromFrame.transmission, toFrame.transmission, t),
    materialType: t < 1 ? fromFrame.materialType : toFrame.materialType,
    transitionMs: clampRange(toFrame.transitionMs, DEFAULT_TRANSITION_MS, 80, 5000),
    backgroundColor: mixHexColors(fromFrame.backgroundColor, toFrame.backgroundColor, t),
    backgroundOpacity: clampOpacity(lerp(fromFrame.backgroundOpacity, toFrame.backgroundOpacity, t), toFrame.backgroundOpacity),
  };
}

function resolveKanvasFrames(item = {}) {
  const box = (item?.box && typeof item.box === 'object') ? item.box : {};
  const baseFrame = normalizeKanvasFrame(box, {
    color1: '#0b0f16',
    color2: '#121b2e',
    gradientColors: ['#0b0f16', '#121b2e'],
    gradientColorAlphas: [1, 1],
    gradientShape: 'linear',
    gradientAngle: 90,
    emissiveColor: '#000000',
    roughness: 0.72,
    metalness: 0.18,
    emissiveIntensity: 0.04,
    opacity: 1,
    clearcoat: 0.08,
    transmission: 0,
    materialType: 'standard',
    transitionMs: DEFAULT_TRANSITION_MS,
    backgroundColor: '#050505',
    backgroundOpacity: 0.65,
  });

  const sourceFrames = Array.isArray(item?.clickAnimations)
    ? item.clickAnimations
    : (Array.isArray(box?.clickAnimations) ? box.clickAnimations : []);

  const clickFrames = sourceFrames
    .slice(0, 12)
    .map((entry) => normalizeKanvasFrame(entry, baseFrame));

  return { baseFrame, clickFrames };
}

function buildViewerGeometry(box = {}) {
  const geometryType = normalizeGeometryType(box.geometryType, 'plane');
  const rotateY = THREE.MathUtils.degToRad(clampRange(box.geometryRotateY, 0, -180, 180));
  const depthScale = clampRange(box.geometryDepth, 1, 0.2, 4);
  const baseSize = 2.15;

  let geometry;

  if (geometryType === 'sphere') {
    geometry = new THREE.SphereGeometry(0.98, 48, 36);
    geometry.scale(baseSize * 0.5, baseSize * 0.5, Math.max(0.28, 0.68 * depthScale));
  } else if (geometryType === 'box') {
    const depth = Math.max(0.18, 0.4 * depthScale);
    geometry = new THREE.BoxGeometry(baseSize, baseSize, depth, 8, 8, 4);
  } else if (geometryType === 'triangle') {
    const depth = Math.max(0.16, 0.34 * depthScale);
    geometry = new THREE.CylinderGeometry(0.96, 0.96, depth, 3, 6);
    geometry.rotateX(Math.PI / 2);
    geometry.rotateZ(Math.PI / 2);
  } else if (geometryType === 'other') {
    geometry = new THREE.IcosahedronGeometry(1.02, 1);
    geometry.scale(baseSize * 0.5, baseSize * 0.5, Math.max(0.32, 0.72 * depthScale));
  } else if (depthScale > 1.04) {
    const depth = Math.max(0.12, 0.24 * depthScale);
    geometry = new THREE.BoxGeometry(baseSize, baseSize, depth, 8, 8, 3);
  } else {
    geometry = new THREE.PlaneGeometry(baseSize, baseSize, 56, 56);
  }

  if (Math.abs(rotateY) > 0.0001) {
    geometry.rotateY(rotateY);
  }

  return geometry;
}

/**
 * GalleryObjectViewer - Renders a focused 3D view of a canvas or text box object
 * Supports click animations, zoom, and pan interactions
 */
function GalleryObjectViewer({ 
  item, 
  isTextBox,
  onClose 
}) {
  const clickAnimationCount = Array.isArray(item?.clickAnimations)
    ? item.clickAnimations.length
    : (Array.isArray(item?.box?.clickAnimations) ? item.box.clickAnimations.length : 0);

  const canvasHostRef = useRef(null);
  const sceneRef = useRef(null);
  const sceneBackdropRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const edgeLinesRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Zoom and rotation state
  const interactionRef = useRef({
    zoom: 1,
    rotationX: -0.08,
    rotationY: -Math.PI / 2,
    isDragging: false,
    lastX: 0,
    lastY: 0,
    animationIndex: 0,
    baseCameraDistance: 3.2,
    meshCenter: new THREE.Vector3(),
  });

  // Initialize Three.js scene
  useEffect(() => {
    if (!item || !canvasHostRef.current) return undefined;

    const hostEl = canvasHostRef.current;

    try {
      // Scene setup
      const scene = new THREE.Scene();
      scene.background = null;
      sceneRef.current = scene;

      const width = Math.max(1, hostEl.clientWidth);
      const height = Math.max(1, hostEl.clientHeight);

      // Camera
      const camera = new THREE.PerspectiveCamera(
        55,
        width / height,
        0.1,
        2000
      );
      cameraRef.current = camera;

      // Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      if ('outputColorSpace' in renderer) {
        renderer.outputColorSpace = 'srgb';
      } else if ('outputEncoding' in renderer) {
        renderer.outputEncoding = THREE.sRGBEncoding;
      }
      renderer.domElement.style.display = 'block';
      renderer.domElement.style.position = 'relative';
      renderer.domElement.style.zIndex = '1';
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      if (hostEl) {
        hostEl.appendChild(renderer.domElement);
      }
      rendererRef.current = renderer;

      // Lighting setup
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.78);
      scene.add(ambientLight);

      const keyLight = new THREE.DirectionalLight(0xffffff, 1.25);
      keyLight.position.set(2.8, 3.2, 3.6);
      scene.add(keyLight);

      const fillLight = new THREE.DirectionalLight(0x66a2ff, 0.48);
      fillLight.position.set(-2, 1, -2);
      scene.add(fillLight);

      const rimLight = new THREE.DirectionalLight(0xffe8b0, 0.28);
      rimLight.position.set(1.2, -1.6, 2.4);
      scene.add(rimLight);

      const { baseFrame, clickFrames } = resolveKanvasFrames(item);
      const squareContentBlocks = normalizeSquareContentBlocks(
        item?.box?.contentBlocks,
        item?.box?.text2 || item?.description || item?.title || ''
      );
      const squareAnimationKeyframes = normalizeSquareAnimationKeyframes(item?.box?.animationKeyframes || item?.box?.keyframes);
      const squareAnimationDurationSeconds = clampRange(item?.box?.animationDuration, 12, 1, 90);
      const squareAnimationMode = normalizeSquareAnimationMode(item?.box?.animationMode, 'linear');
      const squareAnimationDirection = normalizeSquareAnimationDirection(item?.box?.animationDirection, 'loop');

      // Create mesh
      const geometry = buildViewerGeometry(item?.box || {});
      let material = createRuntimeMaterial(baseFrame.materialType);

      const textureCanvas = document.createElement('canvas');
      textureCanvas.width = 1024;
      textureCanvas.height = 1024;
      const textureCtx = textureCanvas.getContext('2d');
      const surfaceTexture = textureCtx ? new THREE.CanvasTexture(textureCanvas) : null;
      if (surfaceTexture) {
        if ('colorSpace' in surfaceTexture) {
          surfaceTexture.colorSpace = 'srgb';
        } else if ('encoding' in surfaceTexture && THREE.sRGBEncoding) {
          surfaceTexture.encoding = THREE.sRGBEncoding;
        }
        surfaceTexture.minFilter = THREE.LinearFilter;
        surfaceTexture.magFilter = THREE.LinearFilter;
        surfaceTexture.generateMipmaps = false;
        material.map = surfaceTexture;
        material.needsUpdate = true;
      }

      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);
      meshRef.current = mesh;

      const edgeGeometry = new THREE.EdgesGeometry(geometry);
      const edgeMaterial = new THREE.LineBasicMaterial({
        color: '#f3f8ff',
        transparent: true,
        opacity: 0.16,
      });
      const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
      edgeLines.renderOrder = 4;
      mesh.add(edgeLines);
      edgeLinesRef.current = edgeLines;

      const drawTextureFrame = (frame, timestampMs) => {
        if (!surfaceTexture || !textureCtx) return;

        const ctx = textureCtx;
        const widthPx = textureCanvas.width;
        const heightPx = textureCanvas.height;
        const titleText = String(item?.title || item?.subject || item?.box?.title || '').trim();
        const fallbackText = String(item?.box?.text2 || item?.description || '').trim();
        const contentBlocks = squareContentBlocks.length
          ? squareContentBlocks
          : normalizeSquareContentBlocks([], fallbackText);
        const anim = interpolateSquareAnimationState(
          squareAnimationKeyframes,
          timestampMs,
          squareAnimationDurationSeconds,
          squareAnimationMode,
          squareAnimationDirection,
        );

        ctx.clearRect(0, 0, widthPx, heightPx);
        ctx.fillStyle = createFrameGradient(ctx, widthPx, heightPx, frame);
        ctx.fillRect(0, 0, widthPx, heightPx);

        const paddingLeft = 64;
        const paddingRight = 64;
        const contentWidth = Math.max(120, widthPx - paddingLeft - paddingRight);
        const baseTitleSize = 46;
        const baseTitleStep = 54;
        const titleRows = titleText
          ? wrapTextWithFont(ctx, titleText, contentWidth, `700 ${baseTitleSize}px system-ui, sans-serif`)
          : [];

        const blockLayouts = contentBlocks.map((block) => {
          const fontSize = Math.max(12, Math.round(block.size));
          const step = Math.max(16, Math.round(fontSize * block.lineHeight));
          const fontFamily = resolveSquareContentFontFamily(block.fontFamily, 'system');
          const font = `${block.italic ? 'italic ' : ''}${block.weight} ${fontSize}px ${fontFamily}`;
          const rows = wrapTextWithFont(ctx, block.text, contentWidth, font);
          return { block, step, font, rows: rows.length ? rows : [''] };
        });

        const titleGap = titleRows.length && blockLayouts.length ? 16 : 0;
        const blockGap = 6;
        let totalHeight = 0;
        if (titleRows.length) totalHeight += titleRows.length * baseTitleStep;
        totalHeight += titleGap;
        blockLayouts.forEach((layout, index) => {
          totalHeight += layout.rows.length * layout.step;
          if (index < blockLayouts.length - 1) totalHeight += blockGap;
        });

        ctx.save();
        ctx.translate(widthPx / 2, heightPx / 2);
        ctx.translate((anim.offsetX * 0.005) * widthPx, (anim.offsetY * 0.005) * heightPx);
        ctx.rotate((anim.rotate * Math.PI) / 180);
        ctx.scale(anim.scale, anim.scale);
        ctx.globalAlpha = clampOpacity(anim.opacity, 1);

        let y = Math.max(68, Math.round((heightPx - totalHeight) / 2));

        if (titleRows.length) {
          ctx.font = `700 ${baseTitleSize}px system-ui, sans-serif`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          for (const row of titleRows) {
            ctx.globalAlpha = clampOpacity(anim.opacity, 1);
            ctx.fillText(row, 0, y - (heightPx / 2), contentWidth);
            y += baseTitleStep;
          }
        }

        y += titleGap;

        blockLayouts.forEach((layout, index) => {
          const align = normalizeSquareContentAlign(layout.block.align, 'center');
          const x = align === 'left'
            ? (paddingLeft - (widthPx / 2))
            : (align === 'right' ? ((widthPx / 2) - paddingRight) : 0);

          ctx.font = layout.font;
          ctx.fillStyle = layout.block.color || '#ffffff';
          ctx.textAlign = align;
          ctx.textBaseline = 'top';
          ctx.globalAlpha = clampOpacity(layout.block.opacity * anim.opacity, 1);

          for (const row of layout.rows) {
            if (row) ctx.fillText(row, x, y - (heightPx / 2), contentWidth);
            y += layout.step;
          }

          if (index < blockLayouts.length - 1) y += blockGap;
        });

        ctx.restore();
        ctx.globalAlpha = 1;
        surfaceTexture.needsUpdate = true;
      };

      const playbackState = {
        activeFrame: baseFrame,
        fromFrame: baseFrame,
        toFrame: baseFrame,
        frameIndex: -1,
        transitionActive: false,
        transitionStartedAt: 0,
        transitionMs: DEFAULT_TRANSITION_MS,
      };

      const applyFrameToScene = (frame) => {
        material = ensureRuntimeMaterial(mesh, material, frame.materialType);
        material.color.set(frame.color1);
        if (material.emissive?.set) {
          material.emissive.set(frame.emissiveColor);
        }
        material.emissiveIntensity = frame.emissiveIntensity;
        if ('roughness' in material) material.roughness = frame.roughness;
        if ('metalness' in material) material.metalness = frame.metalness;
        if ('shininess' in material) {
          material.shininess = clampRange(16 + ((1 - frame.roughness) * 120), 35, 0, 160);
        }
        material.opacity = frame.opacity;
        if ('clearcoat' in material) material.clearcoat = frame.clearcoat;
        if ('transmission' in material) material.transmission = frame.transmission;
        material.transparent = frame.opacity < 0.999 || frame.transmission > 0;
        material.depthWrite = frame.opacity >= 0.2;
        material.needsUpdate = true;

        drawTextureFrame(frame, performance.now());

        edgeMaterial.opacity = frame.opacity < 0.2 ? 0.24 : 0.14;
        edgeMaterial.needsUpdate = true;

        const bgColor = new THREE.Color(frame.color2);
        const colorDelta = colorDistance(bgColor, material.color);
        if (colorDelta < 0.16) {
          const backgroundLuma = relativeLuminance(frame.color2);
          if (backgroundLuma >= 0.5) {
            bgColor.multiplyScalar(0.56);
            edgeMaterial.color.set('#142238');
          } else {
            bgColor.lerp(new THREE.Color('#5f77a3'), 0.32);
            edgeMaterial.color.set('#f5f9ff');
          }
        } else {
          edgeMaterial.color.set(relativeLuminance(frame.color2) > 0.55 ? '#162844' : '#f3f8ff');
        }

        if (sceneBackdropRef.current) {
          sceneBackdropRef.current.style.background = frame.backgroundColor;
          sceneBackdropRef.current.style.opacity = String(clampOpacity(frame.backgroundOpacity, 0.65));
        }

        scene.background = null;
      };

      const resolveCurrentFrame = (timestampMs) => {
        if (!playbackState.transitionActive) {
          return playbackState.activeFrame;
        }

        const durationMs = Math.max(80, playbackState.transitionMs);
        const progress = clampRange((timestampMs - playbackState.transitionStartedAt) / durationMs, 1, 0, 1);
        const current = interpolateFrame(playbackState.fromFrame, playbackState.toFrame, progress);

        if (progress >= 0.999) {
          playbackState.transitionActive = false;
          playbackState.activeFrame = playbackState.toFrame;
          return playbackState.activeFrame;
        }

        return current;
      };

      const meshBounds = new THREE.Box3();
      const meshCenter = new THREE.Vector3();
      const meshSize = new THREE.Vector3();

      const updateCameraForZoom = () => {
        const center = interactionRef.current.meshCenter;
        const zoom = clampRange(interactionRef.current.zoom, 1, 0.4, 8);
        const distance = Math.max(0.36, interactionRef.current.baseCameraDistance / zoom);
        camera.position.set(center.x, center.y, center.z + distance);
        camera.lookAt(center);
        camera.updateProjectionMatrix();
      };

      const frameCameraToMesh = () => {
        meshBounds.setFromObject(mesh);
        meshBounds.getCenter(meshCenter);
        meshBounds.getSize(meshSize);

        const safeWidth = Math.max(0.0001, meshSize.x);
        const safeHeight = Math.max(0.0001, meshSize.y);
        const safeAspect = Math.max(0.0001, camera.aspect || 1);
        const halfFovY = THREE.MathUtils.degToRad(camera.fov * 0.5);
        const distanceForHeight = (safeHeight * 0.5) / Math.max(0.0001, Math.tan(halfFovY));
        const distanceForWidth = (safeWidth * 0.5) / Math.max(0.0001, Math.tan(halfFovY) * safeAspect);
        const fitDistance = Math.max(distanceForHeight, distanceForWidth) * (isTextBox ? 1.02 : 1.18);

        interactionRef.current.meshCenter.copy(meshCenter);
        interactionRef.current.baseCameraDistance = Math.max(1.1, fitDistance);
        updateCameraForZoom();
      };

      const resizeRenderer = () => {
        const nextWidth = Math.max(1, Math.floor(hostEl.clientWidth));
        const nextHeight = Math.max(1, Math.floor(hostEl.clientHeight));
        renderer.setSize(nextWidth, nextHeight, false);
        camera.aspect = nextWidth / nextHeight;
        frameCameraToMesh();
      };

      resizeRenderer();

      let resizeObserver = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          resizeRenderer();
        });
        resizeObserver.observe(hostEl);
      } else {
        window.addEventListener('resize', resizeRenderer);
      }

      // Animation loop
      const animate = () => {
        animationFrameRef.current = requestAnimationFrame(animate);

        if (meshRef.current) {
          meshRef.current.rotation.x = interactionRef.current.rotationX;
          meshRef.current.rotation.y = interactionRef.current.rotationY;
        }

        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          const frame = resolveCurrentFrame(performance.now());
          applyFrameToScene(frame);
          updateCameraForZoom();
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
      };

      const handleWheel = (event) => {
        event.preventDefault();
        const delta = event.deltaY > 0 ? 0.9 : 1.1;
        interactionRef.current.zoom = clampRange(interactionRef.current.zoom * delta, 1, 0.4, 8);
      };

      const handleMouseDown = (event) => {
        interactionRef.current.isDragging = true;
        interactionRef.current.lastX = event.clientX;
        interactionRef.current.lastY = event.clientY;
      };

      const handleMouseMove = (event) => {
        if (!interactionRef.current.isDragging) return;

        const deltaX = event.clientX - interactionRef.current.lastX;
        const deltaY = event.clientY - interactionRef.current.lastY;
        interactionRef.current.rotationY += deltaX * 0.01;
        interactionRef.current.rotationX -= deltaY * 0.01;
        interactionRef.current.rotationX = clampRange(interactionRef.current.rotationX, 0, -Math.PI / 2, Math.PI / 2);

        interactionRef.current.lastX = event.clientX;
        interactionRef.current.lastY = event.clientY;
      };

      const handleMouseUp = () => {
        interactionRef.current.isDragging = false;
      };

      const advanceClickAnimation = (event) => {
        if (event) event.stopPropagation();
        if (!clickFrames.length) return;

        const now = performance.now();
        const currentFrame = resolveCurrentFrame(now);
        const nextIndex = (playbackState.frameIndex + 1) % clickFrames.length;
        const nextFrame = clickFrames[nextIndex];

        playbackState.fromFrame = currentFrame;
        playbackState.toFrame = nextFrame;
        playbackState.frameIndex = nextIndex;
        interactionRef.current.animationIndex = nextIndex;
        playbackState.transitionStartedAt = now;
        playbackState.transitionMs = clampRange(nextFrame.transitionMs, DEFAULT_TRANSITION_MS, 80, 5000);
        playbackState.transitionActive = true;
      };

      renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });
      renderer.domElement.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      renderer.domElement.addEventListener('click', advanceClickAnimation);

      applyFrameToScene(baseFrame);
      frameCameraToMesh();
      renderer.render(scene, camera);

      animate();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        renderer.domElement.removeEventListener('wheel', handleWheel);
        renderer.domElement.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        renderer.domElement.removeEventListener('click', advanceClickAnimation);

        if (resizeObserver) {
          resizeObserver.disconnect();
        } else {
          window.removeEventListener('resize', resizeRenderer);
        }

        if (renderer && hostEl) {
          try {
            hostEl.removeChild(renderer.domElement);
          } catch (e) {
            // Already removed
          }
        }
        renderer.dispose();
        geometry.dispose();
        edgeGeometry.dispose();
        edgeMaterial.dispose();
        surfaceTexture?.dispose?.();
        material.dispose();
      };
    } catch (error) {
      console.error('Error initializing GalleryObjectViewer:', error);
    }
    return undefined;
  }, [item, isTextBox]);

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        width: '420px',
        height: '100vh',
        background: '#0a0a0a',
        borderLeft: '1px solid #222',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
        boxShadow: '-2px 0 10px rgba(0,0,0,0.8)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px',
          borderBottom: '1px solid #222',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item?.subject || item?.title || item?.box?.title || 'Gallery Object'}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '0 8px',
            lineHeight: '1',
            transition: 'color 200ms',
          }}
          onMouseEnter={(e) => (e.target.style.color = '#fff')}
          onMouseLeave={(e) => (e.target.style.color = '#999')}
        >
          X
        </button>
      </div>

      <div
        ref={canvasHostRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          background: 'transparent',
        }}
      >
        <div
          ref={sceneBackdropRef}
          style={{
            position: 'absolute',
            inset: 0,
            background: '#050505',
            opacity: 0.65,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      </div>

      <div
        style={{
          padding: '12px 14px',
          borderTop: '1px solid #222',
          fontSize: '11px',
          color: '#888',
          lineHeight: '1.6',
          background: 'rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ marginBottom: '6px' }}>Drag to rotate | Scroll to zoom</div>
        {clickAnimationCount > 0 && (
          <div>
            Click to animate ({interactionRef.current.animationIndex + 1}/{clickAnimationCount})
          </div>
        )}
      </div>
    </div>
  );
}

export default GalleryObjectViewer;

